import './index.scss'
import text from './text.json'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import {ImgButton} from '../../components/core/button'
import {EditIcon, HorizontalMoreIcon, PausedIcon, PlayIcon, PlusIcon, TrashIcon, LockIcon} from '../../icons'
import ContentLoader from '../../components/core/loader'
import Table from '../../components/core/table'
import EmptyContent from '../resources/EmptyContent'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import Confirm from '../../components/core/confirm'
import {setConfirmIsActive} from '../../stores/alert'
import {Link, useNavigate} from 'react-router-dom'
import {routes} from '../../routes'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {deleteStatuspage, getStatuspages, toggleStatuspageStatus} from '../../stores/statuspage'
import NavigationHelper from '../../utils/navigation'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import Badge from '../../components/core/badge'
import {colors} from '../../utils/colors'
import Tooltip from '../../components/core/tooltip'
import {getSslIconColor, getSslTooltipText} from '../../utils/ssl'
import classNames from 'classnames'

const SORT_ID = 'status-pages'
const customFieldLogic = {
    status: (item) => item?.status ? text.enabled : text.disabled
}
const Statuspages = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [checkedStatuspagesIds, setCheckedStatuspagesIds] = useState([])
    const statuspages = useSelector(state => state.statuspage.statuspages)
    const [sortedData, setSortedData] = useState([])
    const isProcessing = useSelector(state => state.statuspage.isProcessing)
    const isDeleting = useSelector(state => state.statuspage.isDeleting)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [toggleBtnLoader, setToggleBtnLoader] = useState(null)
    const httpsProtocol = 'https://'
    const httpProtocol = 'http://'

    const tableHeader = [
        {
            label: 'Name',
            className: 'min-width-150',
            sorting: {
                asc: {
                    key: 'name_asc',
                    field: ['name']
                },
                desc: {
                    key: 'name_desc',
                    field: ['name']
                }
            }
        },
        {
            label: 'URL',
            className: 'min-width-300',
            sorting: {
                asc: {
                    key: 'url_asc',
                    field: ['subdomain', 'domain']
                },
                desc: {
                    key: 'url_desc',
                    field: ['subdomain', 'domain']
                }
            }
        },
        {
            label: 'Type',
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'type_asc',
                    field: ['type']
                },
                desc: {
                    key: 'type_desc',
                    field: ['type']
                }
            }
        },
        {
            label: 'Status',
            className: 'min-width',
            sorting: {
                asc: {
                    key: 'status_asc',
                    field: ['status']
                },
                desc: {
                    key: 'status_desc',
                    field: ['status']
                }
            }
        }
    ]

    const togglePageStatus = async(id, status) => {
        setToggleBtnLoader({[!status ? text.disabled : text.enabled]: true})
        await dispatch(toggleStatuspageStatus(id, status))
        dispatch(getStatuspages(false))
        setToggleBtnLoader(false)
    }

    const getActionButton = ({id, ...item}) => {
        const isEnabled = item?.status === true
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            <MenuItem onClick={() => {
                navigate(NavigationHelper.getNavigationUrl(routes.edit_statuspage.path, ':id', id))
                setCheckedStatuspagesIds([id])
            }}>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
            <MenuItem onClick={() => togglePageStatus(id, !item?.status)}>
                <span className="d-flex">
                    {isEnabled ? <PausedIcon/> : <PlayIcon/>}
                </span>
                <span>{isEnabled ? text.disable : text.enable}</span>
            </MenuItem>
            <MenuDivider/>
            <MenuItem className="danger" onClick={() => {
                dispatch(setConfirmIsActive(true))
                setCheckedStatuspagesIds([id])
            }
            }>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    const tableBody = sortedData?.map(item => {
        const externalUrl = item?.domain || `${item?.subdomain}.${process.env.REACT_APP_EXTERNAL_STATUSPAGE_DOMAIN}`
        const protocol = (!item?.domain || item?.ssl_cert?.valid) ? httpsProtocol : httpProtocol

        return [
            <span className="min-width-150">{item?.name}</span>,
            <span className="min-width-300 bold-link gap-10">
                <Tooltip content={'SSL certificate: ' + getSslTooltipText(item?.domain, item?.ssl_cert)}>
                    <LockIcon fill={getSslIconColor(item?.domain, item?.ssl_cert)} />
                </Tooltip>
                <a className="underlined" href={protocol + externalUrl} target='_blank'>
                    {externalUrl}
                </a>
            </span>,
            <span className="min-width">{item?.type}</span>,
            <span className="flex-1">
                <span>
                    <Badge color={item?.status ? 'success' : 'warning'}>{item?.status ? text.enabled : text.disabled}</Badge>
                </span>
                <span>{getActionButton(item)}</span>
            </span>
        ]
    })

    const deleteSelectedItem = async() => {
        await dispatch(deleteStatuspage(checkedStatuspagesIds?.[0]))
        dispatch(getStatuspages(false))
        dispatch(setConfirmIsActive(false))
        setCheckedStatuspagesIds([])
    }

    const getIsDisabled = (status) => {
        if (!checkedStatuspagesIds?.length) {
            return true
        }
        const statuspage = statuspages.find(item => item?.id === checkedStatuspagesIds?.[0])
        return status ? statuspage?.status : !statuspage?.status
    }

    useEffect(() => {
        dispatch(getStatuspages())
    }, [])

    useEffect(() => {
        setSortedData(statuspages)
    }, [statuspages])

    return (
        <main className={classNames('main statuspages', {
            dark: isDarkMode
        })}>
            <Confirm
                body={text.are_you_sure}
                showLoader={isDeleting}
                callback={deleteSelectedItem}
            />
            <div>
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                    <ImgButton>
                        <Link to={routes.create_statuspage.path}>
                            {text.add_new_page}
                            <span><PlusIcon/></span>
                        </Link>
                    </ImgButton>
                </div>
                {isProcessing ? (
                    <ContentLoader/>
                ) : sortedData?.length > 0 ? (
                    <div>
                        <div className="search-filter-actions">
                            <div className="selected-actions">
                                <ImgButton className={classNames('outline-red', {
                                    'light-bg': !isDarkMode
                                })}
                                onClick={() => dispatch(setConfirmIsActive(true))}
                                disabled={checkedStatuspagesIds?.length !== 1}
                                >
                                    <span><TrashIcon stroke={colors.colorRed}/></span>
                                    <span className="button-text">{text.delete}</span>
                                </ImgButton>
                                <ImgButton className={classNames({
                                    'light-bg': !isDarkMode
                                })}
                                disabled={toggleBtnLoader?.enabled || getIsDisabled(false)}
                                onClick={() => togglePageStatus(checkedStatuspagesIds?.[0], false)}>
                                    <span>
                                        {toggleBtnLoader?.disabled ? <ButtonLoader/> : <PausedIcon/>}
                                    </span>
                                    <span className="button-text">{text.disable}</span>
                                </ImgButton>
                                <ImgButton className={classNames({
                                    'light-bg': !isDarkMode
                                })}
                                disabled={toggleBtnLoader?.enabled || getIsDisabled(true)}
                                onClick={() => togglePageStatus(checkedStatuspagesIds?.[0], true)}>
                                    <span>
                                        {toggleBtnLoader?.enabled ? <ButtonLoader/> : <PlayIcon/>}
                                    </span>
                                    <span className="button-text">{text.enable}</span>
                                </ImgButton>
                                <ImgButton className={classNames({
                                    'light-bg': !isDarkMode
                                })}
                                onClick={() => navigate(NavigationHelper.getNavigationUrl(routes.edit_statuspage.path, ':id', checkedStatuspagesIds?.[0]))}
                                disabled={checkedStatuspagesIds?.length !== 1}
                                >
                                    <span><EditIcon/></span>
                                    <span className="button-text">{text.edit}</span>
                                </ImgButton>
                            </div>
                        </div>
                        <Table
                            strech
                            tableHeader={tableHeader}
                            tbodyData={tableBody}
                            data={sortedData}
                            identificator='id'
                            inputName='statuspage'
                            isMultiCheckable={false}
                            hasCheckbox={true}
                            selectedData={checkedStatuspagesIds}
                            setSelectedData={data => setCheckedStatuspagesIds(data)}
                            isSortingEnabled
                            setData={setSortedData}
                            sortId={SORT_ID}
                            originalData={statuspages}
                            customFieldLogic={customFieldLogic}
                        />
                    </div>
                ) : <EmptyContent
                    hideButton={true}
                    message={text.empty_content_text}
                />}
            </div>
        </main>
    )
}

export default Statuspages
